import { useEffect, useState, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';
export const useWebSocket = (url) => {
    const [ws, setWs] = useState(null);
    const [reconnectAttempt, setReconnectAttempt] = useState(0);
    const reconnectInterval = useRef(null);
    const heartbeatInterval = useRef(null);
    const websocketRef = useRef(null);
    const disconnectToastId = useRef(null);
    const connect = useCallback(() => {
        if (websocketRef.current && websocketRef.current.readyState === WebSocket.OPEN) {
            return;
        }
        const websocket = new WebSocket(url);
        websocket.onopen = () => {
            setWs(websocket);
            websocketRef.current = websocket;
            setReconnectAttempt(0);
            if (disconnectToastId.current && toast.isActive(disconnectToastId.current)) {
                toast.dismiss(disconnectToastId.current);
            }
            disconnectToastId.current = null;
            if (reconnectInterval.current) {
                clearInterval(reconnectInterval.current);
                reconnectInterval.current = null;
            }
            if (heartbeatInterval.current) {
                clearInterval(heartbeatInterval.current);
            }
            heartbeatInterval.current = setInterval(() => {
                if (websocket.readyState === WebSocket.OPEN) {
                    websocket.send(JSON.stringify({ type: 'pong' }));
                }
            }, 30000); // Send a pong every 30 seconds
        };
        websocket.onerror = (error) => {
        };
        websocket.onclose = (event) => {
            if (!event.wasClean) {
                setReconnectAttempt((prev) => prev + 1);
            }
            if (heartbeatInterval.current) {
                clearInterval(heartbeatInterval.current);
            }
            websocketRef.current = null;
        };
        // Remove the onmessage handler from here
    }, [url]);
    useEffect(() => {
        connect();
        return () => {
            if (websocketRef.current) {
                websocketRef.current.close();
            }
            if (reconnectInterval.current) {
                clearInterval(reconnectInterval.current);
            }
            if (heartbeatInterval.current) {
                clearInterval(heartbeatInterval.current);
            }
        };
    }, [url, connect]);
    useEffect(() => {
        if (reconnectAttempt > 0) {
            reconnectInterval.current = setInterval(() => {
                connect();
            }, Math.min(30000, reconnectAttempt * 5000)); // Exponential backoff up to 30 seconds
        }
    }, [reconnectAttempt, connect, url]);
    // Handle visibility change and focus events
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                connect();
            }
        };
        const handleFocus = () => {
            connect();
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleFocus);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocus);
        };
    }, [connect]);
    // Prompt user to reload if reconnection attempts fail
    useEffect(() => {
        if (reconnectAttempt > 5) {
            if (!disconnectToastId.current || !toast.isActive(disconnectToastId.current)) {
                disconnectToastId.current = toast.error('Connection to the server was lost. Trying to reconnect in the background. You can keep using the page.', { autoClose: false });
            }
        }
    }, [reconnectAttempt]);
    return { ws, reconnectAttempt };
};
